/**
 * The in-house image-collage compositor (ADR-025 A3b). Legacy `ARLMS_ModalWindows.js` parity WITHOUT the server-side FFmpeg:
 * a **"+" right** appends an image horizontally (`hstack`, scaled to the running collage's HEIGHT) and a **"+" bottom**
 * appends vertically (`vstack`, scaled to its WIDTH), separated by a black gap — a **nested fold** where each add wraps the
 * whole running collage. All compositing happens on a `<canvas>` in NATURAL pixels (like the annotate editor); the result is
 * exported to a PNG and saved onto the step via the same `saveEditedStepImage` path (no worker). The outer black frame is
 * applied ONCE at export ({@link frame}) so repeated folds never accumulate nested frames.
 */

export type Direction = "right" | "bottom";

/** The black gap/frame width, scaled to the running collage so it reads consistently at any resolution (legacy used 20px). */
export function borderFor(width: number, height: number): number {
  return Math.max(8, Math.round(Math.min(width, height) / 60));
}

/** A fresh 2D context for a new canvas of `w`×`h` (throws only if the browser can't allocate one — never in practice). */
function newCanvas(w: number, h: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(w));
  canvas.height = Math.max(1, Math.round(h));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas is unavailable");
  return { canvas, ctx };
}

/** The starting base = the step's current image drawn onto a natural-size canvas. */
export function canvasFromImage(img: HTMLImageElement): HTMLCanvasElement {
  const { canvas, ctx } = newCanvas(img.naturalWidth, img.naturalHeight);
  ctx.drawImage(img, 0, 0);
  return canvas;
}

/**
 * Composite `add` onto `base` in `dir`, matching the shared dimension and separated by a `border`-wide black gap. Returns a
 * NEW canvas (the running collage grows); the previous one is discarded by the caller (mirrors the legacy delete-intermediate).
 */
export function composite(base: HTMLCanvasElement, add: HTMLImageElement, dir: Direction, border: number): HTMLCanvasElement {
  if (dir === "right") {
    const addW = add.naturalWidth * (base.height / add.naturalHeight); // scale the new image to the collage's height
    const { canvas, ctx } = newCanvas(base.width + border + addW, base.height);
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(base, 0, 0);
    ctx.drawImage(add, base.width + border, 0, canvas.width - base.width - border, base.height);
    return canvas;
  }
  const addH = add.naturalHeight * (base.width / add.naturalWidth); // scale the new image to the collage's width
  const { canvas, ctx } = newCanvas(base.width, base.height + border + addH);
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(base, 0, 0);
  ctx.drawImage(add, 0, base.height + border, base.width, canvas.height - base.height - border);
  return canvas;
}

/** Wrap the finished collage in a `border`-wide black OUTER frame — applied once at export so folds don't accumulate frames. */
export function frame(base: HTMLCanvasElement, border: number): HTMLCanvasElement {
  const { canvas, ctx } = newCanvas(base.width + border * 2, base.height + border * 2);
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(base, border, border);
  return canvas;
}

/**
 * Load an image for compositing WITHOUT tainting the canvas — `fetch(mode:"cors")` → same-origin blob URL → `<img>` (mirrors
 * the annotate editor's load; a plain crossOrigin `<img>` can reuse a poisoned no-cors cache entry, and we can't cache-bust a
 * presigned URL). Rejects if the fetch is CORS-blocked or the bytes don't decode. The object URL is revoked once decoded.
 */
export function loadImageCors(url: string): Promise<HTMLImageElement> {
  return fetch(url, { mode: "cors", cache: "reload" })
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.blob();
    })
    .then(
      (blob) =>
        new Promise<HTMLImageElement>((resolve, reject) => {
          const objectUrl = URL.createObjectURL(blob);
          const img = new Image();
          img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(img);
          };
          img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error("image failed to decode"));
          };
          img.src = objectUrl;
        }),
    );
}
